"use client";

import { useState } from "react";
import { TwinMark } from "@product/components/app/twin/twin-mark";
import styles from "./explorers.module.css";

const GATES = [
  {
    label: "Discover",
    role: "Source evidence",
    question: "Is there grounded material to define?",
    evidence:
      "Organisation-scoped subscription and billing records, with source provenance.",
    missing: "Source records missing",
    stopped:
      "There is no grounded basis for this definition. A relationship diagram cannot stand in for source evidence.",
    limit: "Finding connected records does not decide what revenue means.",
  },
  {
    label: "Review",
    role: "Explicit meaning",
    question: "Is this what the business means?",
    evidence:
      "The definition states which plans, discounts and contract periods count, with independent evaluation and human review where required.",
    missing: "Meaning unresolved",
    stopped:
      "An unresolved business decision cannot be replaced by a plausible interpretation. Resolve it under the applicable review policy.",
    limit:
      "A review records a decision; it does not prove the definition is universally correct.",
  },
  {
    label: "Validate",
    role: "Declared checks",
    question: "Does the definition hold against known evidence?",
    evidence:
      "Compilation and data checks, including independent reference samples where a containment test is used.",
    missing: "Validation evidence missing",
    stopped:
      "The required validation claim is unsupported. A definition generating its own expected result is not an independent check.",
    limit:
      "Passing a sample check does not prove the definition is universally correct.",
  },
  {
    label: "Activate",
    role: "Explicit promotion",
    question: "Which version is allowed to answer?",
    evidence:
      "A coherent publication, relation, versioned contract and required pinned evidence before activation.",
    missing: "Publication evidence missing",
    stopped:
      "Activation is refused. Publishing a definition and making it active are separate operations.",
    limit:
      "Activation identifies the live definition; it does not certify its semantic truth.",
  },
  {
    label: "Evaluate",
    role: "Recorded verdict",
    question: "How does this version perform on supported questions?",
    evidence:
      "A verdict tied to the definition and evaluation-suite versions, where a suite exists.",
    missing: "Evaluation verdict missing",
    stopped:
      "There is no evaluation verdict to claim. This does not retrospectively undo activation or imply every definition has a suite.",
    limit:
      "A verdict covers the questions tested and the versions used, not every future question.",
  },
] as const;

export default function GatesExplorer() {
  const [selected, setSelected] = useState(0);
  const [missing, setMissing] = useState<number | null>(null);
  const gate = GATES[selected]!;
  const hasEvidence = missing !== selected;
  return (
    <div className={styles.explorer}>
      <div className={styles.eyebrow}>
        <TwinMark /> 01 / Inspect the controls
      </div>
      <h2 className={styles.heading}>
        Five questions. Different kinds of proof.
      </h2>
      <p className={styles.intro}>
        Follow a recurring-revenue definition. Select a control, then remove its
        evidence to see what can no longer be claimed.
      </p>
      <div
        className={styles.gateList}
        role="group"
        aria-label="Governance controls"
      >
        {GATES.map((item, i) => (
          <button
            key={item.label}
            aria-pressed={selected === i}
            data-state={missing === i ? "stopped" : "present"}
            onClick={() => setSelected(i)}
          >
            <small>0{i + 1}</small>
            <strong>{item.label}</strong>
            <small>{missing === i ? "Evidence missing" : item.role}</small>
          </button>
        ))}
      </div>
      <div className={styles.gateBody}>
        <span className={styles.eyebrow}>{gate.role}</span>
        <h3>{gate.question}</h3>
        <p>{gate.evidence}</p>
        <button
          className={styles.button}
          aria-pressed={!hasEvidence}
          onClick={() => setMissing(hasEvidence ? selected : null)}
        >
          {hasEvidence ? "Remove this evidence" : "Restore this evidence"}
        </button>
        <div className={styles.gateState} role="status">
          <strong>
            {hasEvidence ? "Evidence present in this example" : gate.missing}
          </strong>
          {hasEvidence ? gate.limit : gate.stopped}
        </div>
      </div>
      <div className={styles.limit}>
        Five illustrative questions, not a literal release pipeline. Contract
        authoring includes independent evaluation before admission.
        Supported-question evaluations separately test how an agent uses a
        definition.
      </div>
    </div>
  );
}
